import { useState } from "react";
import { ShieldCheck, ShieldAlert, Trash2, Eye, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BTN_SOFT, BTN_SECONDARY } from "@/components/checkout/buttonStyles";

const REVOKE_ROLES = ["super_admin", "platform_admin", "hotel_admin", "hotel_manager", "department_head"];
const SM = "h-8 px-2.5 text-[12px]";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null);
const fileUrl = (u) => (u?.startsWith("/api") ? `${API_BASE}${u.slice(4)}` : u);

export function VerificationBadge({ status }) {
  const ok = status === "verified";
  return (
    <span data-testid="doc-verification-badge" data-status={ok ? "verified" : "unverified"}
      className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${ok ? "bg-[#DBFCEF] text-[#014751]" : "bg-[#FEF3C7] text-[#78530E]"}`}>
      {ok ? <ShieldCheck size={12} strokeWidth={2} className="text-[#14C48E]" /> : <ShieldAlert size={12} strokeWidth={2} className="text-[#F59E0B]" />}
      {ok ? "Verified" : "Unverified"}
    </span>
  );
}

/** Verify / Revoke controls for one government document (profile entry or uploaded file). */
export function VerifyControls({ guestId, docId, doc, label, onChanged, compact = false }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const verified = doc.verification_status === "verified";
  const canRevoke = REVOKE_ROLES.includes(user?.role);

  const verify = async () => {
    setBusy(true);
    try { await api.patch(`/config/guests/${guestId}/documents/${docId}/verify`, {}); toast.success(`${label} marked as verified`); setMode(null); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const revoke = async () => {
    if (reason.trim().length < 3) { toast.error("Please give a reason"); return; }
    setBusy(true);
    try { await api.patch(`/config/guests/${guestId}/documents/${docId}/revoke-verification`, { reason: reason.trim() }); toast.success("Verification revoked"); setMode(null); setReason(""); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (mode === "verify") {
    return (
      <div className="rounded-md border border-[#37F4B5] bg-[#E6FEFF] px-3 py-2 text-[12px] text-[#014751] space-y-2" data-testid="verify-confirm">
        <div><span className="font-semibold">Mark this {label} as verified?</span> Confirm that you have physically inspected the original document.</div>
        <div className="flex gap-2">
          <button type="button" className={`${BTN_SECONDARY} ${SM}`} onClick={() => setMode(null)} data-testid="verify-cancel">Cancel</button>
          <button type="button" className={`${BTN_SOFT} ${SM}`} onClick={verify} disabled={busy} data-testid="verify-yes">{busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}Yes, Verify</button>
        </div>
      </div>
    );
  }
  if (mode === "revoke") {
    return (
      <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[12px] text-[#78530E] space-y-2" data-testid="revoke-form">
        <div className="font-semibold">Revoke verification — why?</div>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. verified the wrong document" className="w-full h-8 rounded border border-[#D5E6E8] px-2 text-[12px] text-[#1A2425]" data-testid="revoke-reason" />
        <div className="flex gap-2">
          <button type="button" className={`${BTN_SECONDARY} ${SM}`} onClick={() => setMode(null)} data-testid="revoke-cancel">Cancel</button>
          <button type="button" className={`${BTN_SECONDARY} ${SM} !text-[#991B1B] !border-[#FECACA]`} onClick={revoke} disabled={busy} data-testid="revoke-yes">{busy ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />}Revoke</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {!verified && <button type="button" className={`${BTN_SOFT} ${SM}`} onClick={() => setMode("verify")} data-testid="mark-verified-btn"><ShieldCheck size={13} />{compact ? "Verify" : "Mark as Verified"}</button>}
      {verified && canRevoke && <button type="button" className={`${BTN_SECONDARY} ${SM}`} onClick={() => setMode("revoke")} data-testid="revoke-verification-btn"><Undo2 size={13} />Revoke Verification</button>}
    </div>
  );
}

/** One row in Guest → Documents: either a profile government_documents entry or an uploaded guest_documents file. */
export function GovtDocRow({ guestId, doc, label, docId, onChanged, onDelete, testid }) {
  const verified = doc.verification_status === "verified";
  const added = fmt(doc.added_at || doc.collected_at || doc.created_at);
  const addedBy = doc.added_by_name || doc.collected_by_name;
  return (
    <div className="p-3 bg-hocp-bgCard border border-hocp-border rounded-lg space-y-2" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13.5px] font-semibold text-[#1A2425]" data-testid={`${testid}-label`}>{label}</span>
            {doc.number && <span className="mono text-[12px] text-hocp-textPrimary">{doc.number}</span>}
            {doc.file_name && <span className="mono text-[11px] text-hocp-textMuted truncate max-w-[220px]">{doc.file_name}</span>}
            {doc.expiry_date && <span className="text-[11px] mono text-hocp-textMuted">exp {doc.expiry_date}</span>}
          </div>
          <div className="text-[11.5px] text-hocp-textMuted mt-0.5" data-testid={`${testid}-meta`}>
            {added ? `Added: ${added}` : "Added"}{addedBy ? ` · by ${addedBy}` : ""}
            {verified && doc.verified_at ? ` · Verified: ${fmt(doc.verified_at)}${doc.verified_by_name ? ` by ${doc.verified_by_name}` : ""}` : ""}
            {!verified && doc.revoked_at ? ` · Revoked: ${fmt(doc.revoked_at)}${doc.revoked_by_name ? ` by ${doc.revoked_by_name}` : ""}${doc.revoke_reason ? ` — ${doc.revoke_reason}` : ""}` : ""}
          </div>
        </div>
        <VerificationBadge status={doc.verification_status} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {doc.file_url && <a href={fileUrl(doc.file_url)} target="_blank" rel="noreferrer" className={`${BTN_SECONDARY} ${SM}`} data-testid={`${testid}-view`}><Eye size={13} />View</a>}
        <VerifyControls guestId={guestId} docId={docId} doc={doc} label={label} onChanged={onChanged} />
        {onDelete && <button type="button" onClick={onDelete} className={`${BTN_SECONDARY} ${SM} !text-[#991B1B]`} data-testid={`${testid}-delete`}><Trash2 size={13} />Delete</button>}
      </div>
    </div>
  );
}
